import { Controller, useFormContext } from 'react-hook-form';
import { Autocomplete } from '@ds';
import { AdminFormRow } from '@ui';
import { EmployeeFormValues } from '@/types';
import { AirportType } from '@/api/apis/facilities';
import { renderAutocompleteItem } from '@/utils/helpers';
import { useInfiniteAirportsListQueryByName } from '@/queries/airports';
import { useInfiniteDropdown } from '@/hooks/useInfiniteDropdown';
import { ControlledHubAutocomplete } from '../../selectors/ControlledHubAutocomplete';
import { LocationPreferenceSelector } from './LocationPreferenceSelector';

const airportsMapper = (item: AirportType) => ({
  value: `${item.maoId}`,
  name: item.name,
});

type BaseLocationFieldsProps = {
  isReadOnly?: boolean;
  isBaseLocationRequired: boolean;
  onBaseLocationTimezoneChange: (tz: string) => void;
  // Contractor-only props
  isContractor?: boolean;
  initialAirportName?: string;
};

export const BaseLocationFields = ({
  isReadOnly,
  isBaseLocationRequired,
  onBaseLocationTimezoneChange,
  isContractor,
  initialAirportName,
}: BaseLocationFieldsProps) => {
  const { control, setValue } = useFormContext<EmployeeFormValues>();

  const {
    searchValue: airportSearchValue,
    list: airportsList,
    isLoading: isAirportsListLoading,
    onSearchValue: onAirportSearch,
    setAutocompleteOpen: setAirportsAutocompleteOpen,
    itemsScrollerRef: airportsScrollerRef,
    onItemSelectChange: onAirportSelectChange,
  } = useInfiniteDropdown({
    setValue: (v) => setValue('closestAirportForDeploymentId', v),
    listQuery: useInfiniteAirportsListQueryByName,
    mapper: airportsMapper,
    initialName: initialAirportName,
  });

  return (
    <>
      <AdminFormRow title="Base Location" isRequired={isBaseLocationRequired}>
        <ControlledHubAutocomplete
          name="baseLocationId"
          aria-label="Base Location"
          isDisabled={isReadOnly}
          onSelectionChange={(hub) => onBaseLocationTimezoneChange(hub?.location.timezone || '')}
        />
      </AdminFormRow>

      {isContractor && (
        <AdminFormRow title="Closest Airport For Deployment">
          <Controller
            name="closestAirportForDeploymentId"
            control={control}
            render={({ field: { ref, value, onChange, ...field }, fieldState: { invalid, error } }) => (
              <Autocomplete
                allowsCustomValue
                placeholder="Choose an option"
                aria-label="Closest Airport For Deployment"
                scrollRef={airportsScrollerRef}
                items={airportsList}
                isLoading={isAirportsListLoading}
                onInputChange={onAirportSearch}
                inputValue={airportSearchValue}
                isDisabled={isReadOnly}
                isInvalid={invalid}
                errorMessage={error?.message}
                onOpenChange={(isOpen) => setAirportsAutocompleteOpen(isOpen)}
                selectedKey={value}
                value={value || ''}
                onSelectionChange={(key) => onAirportSelectChange(key as string)}
                {...field}
              >
                {renderAutocompleteItem}
              </Autocomplete>
            )}
          />
        </AdminFormRow>
      )}

      <LocationPreferenceSelector isReadOnly={isReadOnly} isContractor={isContractor} />
    </>
  );
};
